'use client';

import useGlobalState from '@hooks/useGlobalState';
import MainLayout from '@wconcept/wconcept-ui-renewal/components/layouts/MainLayout';
import SubLayout from '@wconcept/wconcept-ui-renewal/components/layouts/SubLayout';
import { usePathname, useRouter } from 'next/navigation';
import {
  NewGnbInfo,
  SubMenuListItem,
} from '@wconcept/wconcept-common/models/renewal/gnb';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  addAsIsMWebDomain,
  currentUrl,
} from '@wconcept/wconcept-common/utils/domainUtil';
import useHistoryRouter from '@hooks/useLinkTo';
import {
  getCookieWckRPImage,
  getCookieWckStyleClipNo,
} from '@wconcept/wconcept-common/utils/cookie';
import useModalManager from '@hooks/useModalManager';
import { checkAppVersion } from '@wconcept/wconcept-common/utils/userAgentUtil';
import { BiLogo } from '@wconcept/wconcept-common/models/renewal/popup';
import useGa4Event from '@hooks/useGa4Event';
import useBrazeLogger from '@hooks/useBrazeLogger';
import CustomErrorBoundary from '@components/common/CustomErrorBoundary';
import { useNavigationHandlers } from '@hooks/useNavigationHandlers';
// import { useAsyncError } from '@hooks/useAsyncError';

interface IHeaderLayout {
  className?: string;
  children?: React.ReactNode;
  titleNode?: React.ReactNode;
  gnbInfo?: NewGnbInfo[];
  selectedGnbValue?: string;
  isFooter?: boolean;
  isTopBtn?: boolean;
  pageTitle?: string;
  gnbType?: string;
  subHeaderType?: 'main' | 'subPage' | 'subDetail' | undefined;
  headerBgType?: 'black' | 'transparent' | undefined;
  actionbarBgType?: 'black';
  actionbarActive?: 'discovery';
  isHistoryBtn?: boolean;
  isShortFormBtn?: boolean | 'simple';
  isStyleClipAddBtn?: boolean;
  isProfileBtn?: boolean;
  isShortFormBtnTooltip?: boolean;
  setTooltipShown?: (value: string) => void;
  styleClipTooltipPoint?: number;
  isSearchBtn?: boolean;
  isCartBtn?: boolean;
  isShareBtn?: boolean;
  logoInfo?: BiLogo;
  onShortFormBtnToolTip?: () => void;
  onClickTopBtn?: () => void;
  onShortFormBtnClick?: () => void;
  onClickShare?: () => Promise<void>;
  gnbStyleType?: 'default' | 'liquidGlass' | undefined;
  transparentHeader?: boolean;
}

function HeaderLayout({
  children,
  titleNode,
  selectedGnbValue: _selectedGnbValue,
  isFooter,
  isTopBtn,
  gnbInfo,
  className,
  pageTitle,
  gnbType,
  subHeaderType,
  headerBgType,
  actionbarBgType,
  actionbarActive,
  isHistoryBtn,
  isShortFormBtn,
  isStyleClipAddBtn,
  isProfileBtn,
  isShortFormBtnTooltip,
  styleClipTooltipPoint,
  setTooltipShown,
  isSearchBtn,
  isCartBtn,
  isShareBtn,
  logoInfo,
  onClickTopBtn,
  onShortFormBtnToolTip,
  onShortFormBtnClick,
  onClickShare,
  gnbStyleType,
  transparentHeader,
}: IHeaderLayout) {
  const { datadogProperty, appInfo, assistData, deviceInfo } = useGlobalState();
  const { linkTo } = useHistoryRouter();
  const gnbTypes = gnbType || '';
  const [historyImg, setHistoryImg] = useState('');
  const pathname = usePathname();
  const { openLoginConfirm } = useModalManager();
  const { logCustomEvent } = useBrazeLogger();
  const {
    onClickCate,
    onClickMyheart,
    onClickSearch,
    onClickMypage,
    onClickCartBtn,
    onClickSearchBtn,
    onClickBackBtn,
  } = useNavigationHandlers();
  // useAsyncError();

  const {
    sendGa4HeaderClick,
    sendGa4ActionBarClick,
    sendGa4GnbClick,
    sendGa4FooterClick,
    sendGa4ButtonClick,
  } = useGa4Event();

  const executeHapticWithLinkTo = useCallback(
    (additionalAction?: () => void) => {
      if (appInfo?.isNativeAppBoth) {
        window.location.href = 'wconceptkr://actionBarHaptic';
      }
      setTimeout(() => {
        additionalAction?.();
      }, 100);
    },
    [appInfo?.isNativeAppBoth]
  );

  const getHistoryImg = useCallback(async () => {
    const imgUrl = await getCookieWckRPImage();
    if (!assistData?.isLogin) {
      setHistoryImg('');
      return;
    }
    setHistoryImg(imgUrl);
  }, [assistData]);

  useEffect(() => {
    getHistoryImg();
  }, [getHistoryImg]);

  const onClickMenuBar = useCallback(
    (value: NewGnbInfo, no: number) => {
      sendGa4GnbClick({
        text: value.menuTitle,
        area: 'gnb_list_tab',
        page: 'gnb',
        listNo: no.toString(),
        adYn: value.adYn,
      });

      let targetUrl = value?.subMenuList?.[0]?.landingUrl || value?.landingUrl;
      // window의 값이 Y일 경우 새창으로 이동하는 것이므로 gnbType을 N으로 변경
      if (value?.window === 'Y' && targetUrl) {
        const url = new URL(targetUrl);
        url?.searchParams?.set('gnbType', 'N');
        targetUrl = url?.toString();
      }

      linkTo.push({
        url: targetUrl,
      });
    },
    [linkTo, sendGa4GnbClick]
  );

  const onClickSubMenuBar = useCallback(
    (value: SubMenuListItem, no: number) => {
      sendGa4GnbClick({
        text: value.menuTitle,
        area: 'gnb_sublist_tab',
        page: 'gnb',
        listNo: no.toString(),
      });

      linkTo.push({
        url: value.landingUrl,
      });
    },
    [linkTo, sendGa4GnbClick]
  );

  const onClickFooter = useCallback(
    (type: string) => {
      sendGa4FooterClick({ text: type });
    },
    [sendGa4FooterClick]
  );

  const onClickHome = useCallback(() => {
    sendGa4ActionBarClick({ text: 'home' });

    executeHapticWithLinkTo(() => {
      if (appInfo.isNativeAppBoth) {
        window.location.href = 'wconceptkr://actionBarHome';
      } else if (pathname === '/rn/women') {
        window.location.reload();
      } else {
        linkTo.push({
          url: '/rn/women',
        });
      }
    });
  }, [
    appInfo.isNativeAppBoth,
    executeHapticWithLinkTo,
    linkTo,
    pathname,
    sendGa4ActionBarClick,
  ]);

  const onClickLogoBtn = useCallback(() => {
    sendGa4HeaderClick({ text: 'logo' });
    if (logoInfo?.landingUrl) {
      linkTo.push({
        url: logoInfo.landingUrl,
      });
    } else if (pathname === '/rn/women') {
      window.location.reload();
    } else {
      linkTo.push({
        url: '/rn/women',
      });
    }
  }, [linkTo, logoInfo, pathname, sendGa4HeaderClick]);

  const onClickHistory = useCallback(async () => {
    sendGa4ActionBarClick({ text: 'history' });

    // 7.0.0 버전 미만 일경우
    if (checkAppVersion(deviceInfo?.version, '7.0.0') === 'low') {
      executeHapticWithLinkTo(() => {
        if (!assistData?.isLogin) {
          openLoginConfirm(
            `${process.env.NEXT_PUBLIC_WCONCEPT_DISPLAY_WEB}/rn/history`,
            true
          );
          return;
        }
        linkTo.push({
          url: '/rn/history',
        });
      });
    } else {
      if (!assistData?.isLogin) {
        openLoginConfirm(
          `${process.env.NEXT_PUBLIC_WCONCEPT_DISPLAY_WEB}/rn/history`,
          true
        );
        return;
      }
      linkTo.push({
        url: '/rn/history',
      });
    }
  }, [
    assistData?.isLogin,
    deviceInfo?.version,
    executeHapticWithLinkTo,
    linkTo,
    openLoginConfirm,
    sendGa4ActionBarClick,
  ]);

  const onClickShortForm = useCallback(() => {
    if (actionbarActive === 'discovery') {
      onShortFormBtnClick?.();
      linkTo.push({
        url: '/rn/discovery/shortform',
      });
      return;
    }
    logCustomEvent({
      eventName: 'click_styleclip_shortform',
    });
    sendGa4ButtonClick({
      area: 'styleclip_floating_videoview_button',
      text: '영상모아보기',
      tabNm: pageTitle,
    });
    linkTo.push({
      url: '/rn/styleclip/shortform',
    });
  }, [
    actionbarActive,
    logCustomEvent,
    sendGa4ButtonClick,
    pageTitle,
    linkTo,
    onShortFormBtnClick,
  ]);

  const onClickProfileBtn = useCallback(async () => {
    sendGa4HeaderClick({ text: '스타일클립 프로필' });
    const styleClipNo = await getCookieWckStyleClipNo();
    if (assistData?.isLogin) {
      if (styleClipNo && styleClipNo !== '0') {
        linkTo.push({
          url: `/rn/styleclip/profile/${styleClipNo}`,
        });
      } else {
        const returnUrl = `${await addAsIsMWebDomain(
          '/MyPage/MyProfile'
        )}?rUrl=${encodeURIComponent(currentUrl())}`;

        linkTo.push({
          url: returnUrl,
        });
      }
    } else {
      openLoginConfirm();
    }
  }, [assistData, linkTo, openLoginConfirm, sendGa4HeaderClick]);

  const onClickStyleClipAddBtn = useCallback(() => {
    sendGa4ButtonClick({
      text: '등록하기',
      tabNm: pageTitle === '스타일클립' ? '스타일클립 등록' : pageTitle,
      area: 'styleclip_floating_register_button',
    });
    if (assistData?.isLogin) {
      linkTo.push({
        url: '/rn/styleclip/create',
      });
    } else {
      openLoginConfirm();
    }
  }, [
    assistData?.isLogin,
    linkTo,
    pageTitle,
    openLoginConfirm,
    sendGa4ButtonClick,
  ]);

  const getPathnameFromUrl = useCallback((url?: string) => {
    if (!url) return '';

    try {
      return new URL(url).pathname;
    } catch {
      return url.split('?')[0] || '';
    }
  }, []);

  const subGnbList = useMemo(() => {
    if (!gnbInfo?.length) return undefined;

    const currentGnb = gnbInfo.find((v: NewGnbInfo) => {
      if (!v.subMenuList?.length) return false;

      return v.subMenuList.some(subMenu => {
        try {
          return getPathnameFromUrl(subMenu.landingUrl) === pathname;
        } catch {
          return false;
        }
      });
    });

    return currentGnb?.subMenuList || undefined;
  }, [getPathnameFromUrl, gnbInfo, pathname]);

  // ITEM RENDERER : getHeaderTypeRender
  return gnbTypes === 'Y' ? (
    <MainLayout
      className={className}
      gnbType={gnbStyleType}
      headerBgType={headerBgType}
      gnbList={gnbInfo}
      selectedGnbValue={_selectedGnbValue || pathname}
      selectedSubGnbValue={pathname}
      isFooter={isFooter}
      isLogin={assistData.isLogin || false}
      agentOnBoardingYn={datadogProperty?.agentOnBoardingYn}
      basketCount={parseInt(
        assistData?.basketCount ? assistData?.basketCount : '0',
        10
      )}
      subGnbList={subGnbList}
      logoInfo={logoInfo}
      historyBtnRs="88"
      isNativeAppBoth={appInfo?.isNativeAppBoth}
      appVersion={deviceInfo?.version}
      isIPhoneXApp={appInfo?.isIPhoneXApp}
      historyImg={historyImg}
      isShortFormBtnTooltip={isShortFormBtnTooltip}
      setTooltipShown={setTooltipShown}
      handleClickMenuBar={onClickMenuBar}
      handleClickSubMenuBar={onClickSubMenuBar}
      getHistoryImg={getHistoryImg}
      onClickHome={onClickHome}
      onClickCate={onClickCate}
      // onClickDiscovery={onClickDiscovery}
      onClickSearch={onClickSearch}
      onClickMyheart={onClickMyheart}
      onClickMypage={onClickMypage}
      onClickHistory={onClickHistory}
      isShareBtn={isShareBtn}
      onClickShare={onClickShare}
      onClickSearchBtn={onClickSearchBtn}
      onClickCartBtn={onClickCartBtn}
      onClickLogoBtn={onClickLogoBtn}
      onClickStyleClipAddBtn={onClickStyleClipAddBtn}
      onClickFooter={onClickFooter}
    >
      <CustomErrorBoundary>{children}</CustomErrorBoundary>
    </MainLayout>
  ) : (
    <SubLayout
      className={className}
      title={pageTitle}
      titleNode={titleNode}
      logoInfo={logoInfo}
      historyBtnRs="88"
      headerType={subHeaderType}
      isLogin={assistData.isLogin || false}
      isTopBtn={isTopBtn}
      isNativeAppBoth={appInfo?.isNativeAppBoth}
      isIPhoneXApp={appInfo?.isIPhoneXApp}
      getHistoryImg={getHistoryImg}
      historyImg={historyImg}
      basketCount={parseInt(
        assistData?.basketCount ? assistData?.basketCount : '0',
        10
      )}
      headerBgType={headerBgType}
      actionbarBgType={actionbarBgType}
      actionbarActive={actionbarActive}
      isHistoryBtn={isHistoryBtn}
      isShareBtn={isShareBtn}
      isShortFormBtn={isShortFormBtn}
      isStyleClipAddBtn={isStyleClipAddBtn}
      isProfileBtn={isProfileBtn}
      isShortFormBtnTooltip={isShortFormBtnTooltip}
      setTooltipShown={setTooltipShown}
      isSearchBtn={isSearchBtn}
      isCartBtn={isCartBtn}
      appVersion={deviceInfo?.version}
      styleClipTooltipPoint={styleClipTooltipPoint}
      transparentHeader={transparentHeader}
      onClickStyleClipAddBtn={onClickStyleClipAddBtn}
      onClickHome={onClickHome}
      onClickCate={onClickCate}
      // onClickDiscovery={onClickDiscovery}
      onClickSearch={onClickSearch}
      onClickMyheart={onClickMyheart}
      onClickMypage={onClickMypage}
      onClickBackBtn={onClickBackBtn}
      onClickSearchBtn={onClickSearchBtn}
      onClickCartBtn={onClickCartBtn}
      onClickHistory={onClickHistory}
      onClickTop={onClickTopBtn}
      onClickShare={onClickShare}
      onClickShortForm={onClickShortForm}
      onClickProfileBtn={onClickProfileBtn}
    >
      <CustomErrorBoundary>{children}</CustomErrorBoundary>
    </SubLayout>
  );
}

export default HeaderLayout;
